/**
 * Auto-Pilot registry — v3.
 *
 * ACTIVE_APS: pre-configured AP instances shown in the "Aktiv" tab.
 * ALL_TEMPLATES: all available AP templates shown in the "Alle" tab
 *   (the 4 active APs in raw template form + 2 template-only extras).
 *
 * v3 elevates apType from metadata to a structural constraint and adds
 * scope + chat to every AP definition.
 */

import {
  IconClipboardList,
  IconFileExport,
  IconFileImport,
  IconRadar,
  IconSettings2,
  IconTruck,
  IconUserCheck,
  type Icon as TablerIconType,
} from "@tabler/icons-react"
import type { ComponentType } from "react"
import type { ScopeListItem } from "../../types"
import { ConfigurationAP } from "./configuration/ConfigurationAP"
import { CustomerConformityAktionenTab } from "./customer-conformity/CustomerConformityAktionenTab"
import { SubstanzRadarAktionenTab } from "./monitor/SubstanzRadarAktionenTab"
import { SharedDatenbankTab } from "./shared/DatenbankTab"
import { SupplierInquiryAktionenTab } from "./supplier-inquiry/SupplierInquiryAktionenTab"
import { SupplierAktionenTab } from "./supplier/SupplierAktionenTab"

// ── Shared types ──────────────────────────────────────────────────────────────

export type ScopeId = string
export type ApType = "configurator" | "importer" | "exporter" | "monitor"

// Minimal store slice needed for unlock logic
export type StoreSnapshot = { dbConfigured: boolean | null }

// Full store slice needed for card notifications + preview stats
export interface APStoreData {
  dbConfigured: boolean | null
  scopes: ScopeListItem[]
  totalRecordCount: number
  scopeRecordCounts: Record<string, number>
  /** Currently-selected record IDs in the supplier scope (importer-AP selection model). */
  selectedSupplierRecordIds: Set<string>
  /** Total pending documents per AP id (refreshed via React Query in Landing). */
  pendingDocCountByAp: Record<string, number>
  /** Pending docs uploaded after the last time each AP was opened (for notification badge). */
  newDocCountByAp: Record<string, number>
  /** Open (pending/partially_applied) finding counts per monitor AP id. */
  monitorOpenCountByAp: Record<string, number>
  /** Customers that became fresh since the customer-conformity AP was last opened. */
  customerNewFreshCount: number
  /** Total customers currently inside the freshness filter (ready-to-export). */
  customerFreshCount: number
  /** Per-AP count of cases in "Aktion fällig" state — drives the red AP-tile badge. */
  cockpitDueCountByAp?: Record<string, number>
  /** Per-AP open APWorkItem count (Ausstehend section). */
  ausstehendCountByAp?: Record<string, number>
}

export interface HeroState {
  dbConfigured: boolean | null
  scopes: ScopeListItem[]
  hasActiveDiffSession: boolean
}

export interface PreviewStat {
  label: string
  value: string | number
}

export interface LockRequirement {
  label: string
  isMet: (data: APStoreData) => boolean
  /** Color shown when isMet() is false. green is used when isMet() is true. */
  pendingColor: "yellow" | "red"
}

// ── Interface definitions ─────────────────────────────────────────────────────

// Monitor-AP criteria config (display-only in v6; DSL stored in registry)
export interface MonitorCriteria {
  rules: { field: string; op: string; value: string }[]
  combinator: "any" | "all"
  evidenceSources: string[]
}

// Full AP instance — has functional tab components
export interface AutoPilotDef {
  id: string
  name: string
  description: string // one-sentence "core question" this AP answers
  systemPrompt: string // instructions shown in AP settings as "Arbeitsablauf"
  icon: TablerIconType
  apType: ApType

  // v3: scope constraints — what the agent + Datenbank tab may see
  scope: {
    primaryScopeId: ScopeId | null
    visibleScopeIds: ScopeId[] // empty = show all (Configuration AP)
  }

  // v3: chat intent dictionary
  chat: {
    systemPromptKey: string
    knownIntents: string[]
  }

  // v6: monitor-type configuration (only present when apType === 'monitor')
  monitor?: MonitorCriteria

  unlockCondition: (s: StoreSnapshot) => boolean
  lockRequirements: LockRequirement[]
  getNotifications: (data: APStoreData) => number
  getPreviewStats: (data: APStoreData) => PreviewStat[]
  hero: (state: HeroState) => string
  AktionenTab: ComponentType
  DatenbankTab: ComponentType
}

// Template-only entry — visual info for the "Alle" tab
export interface AutoPilotTemplate {
  id: string
  name: string
  description: string
  icon: TablerIconType
  apType: ApType
  /** Primary scope this AP operates on; null for Configuration AP. */
  primaryScopeId?: ScopeId | null
}

// ── Scope → background icon mapping (v4.1) ────────────────────────────────────
// Card background icon is derived from the AP's primary scope, not from ap.icon.

const SCOPE_BG_ICON: Record<string, TablerIconType> = {
  supplier: IconTruck,
  customer: IconUserCheck,
  substance: IconRadar,
}

export function getScopeBackgroundIcon(
  primaryScopeId: ScopeId | null | undefined,
): TablerIconType {
  if (!primaryScopeId) return IconSettings2
  return SCOPE_BG_ICON[primaryScopeId] ?? IconClipboardList
}

// ── Active AP instances (Aktiv tab) ───────────────────────────────────────────

export const ACTIVE_APS: AutoPilotDef[] = [
  ConfigurationAP,
  {
    id: "supplier",
    name: "QMS/AP/SUPPLIER/NAME",
    description: "QMS/AP/SUPPLIER/DESCRIPTION",
    systemPrompt:
      "You are a supplier management assistant. " +
      "You analyze supplier declarations and identify overdue or incomplete submissions. " +
      "When anomalies are found, you suggest concrete measures to keep the supply chain compliant. " +
      "Always refer to the stored supplier and BOM data.",
    icon: IconTruck,
    apType: "importer",
    scope: {
      primaryScopeId: "supplier",
      visibleScopeIds: ["supplier", "component", "substance"],
    },
    chat: {
      systemPromptKey: "supplier",
      knownIntents: [
        "analyse_supplier_declaration",
        "which_suppliers_are_overdue",
      ],
    },
    unlockCondition: (s) => s.dbConfigured === true,
    lockRequirements: [
      {
        label: "QMS/REQUIREMENT/CONFIGURE_DATABASE",
        isMet: (d) => d.dbConfigured === true,
        pendingColor: "yellow",
      },
    ],
    getNotifications: (data) => data.newDocCountByAp["supplier"] ?? 0,
    getPreviewStats: (d) => {
      if (!d.scopes.some((s) => s.id === "supplier")) return []
      const totalSuppliers = d.scopeRecordCounts["supplier"] ?? 0
      const selectedSuppliers = d.selectedSupplierRecordIds.size
      const pending = d.pendingDocCountByAp["supplier"] ?? 0
      return [
        {
          label: "QMS/PREVIEW/IN_PROGRESS",
          value: `${selectedSuppliers}/${totalSuppliers}`,
        },
        { label: "QMS/PREVIEW/DOCUMENTS", value: pending },
      ]
    },
    hero: () => "Supplier Declarations — Auto-Pilot",
    AktionenTab: SupplierAktionenTab,
    DatenbankTab: SharedDatenbankTab,
  },
  {
    id: "customer-conformity",
    name: "QMS/AP/CUSTOMER_CONFORMITY/NAME",
    description: "QMS/AP/CUSTOMER_CONFORMITY/DESCRIPTION",
    systemPrompt:
      "You are a customer conformity assistant. " +
      "You analyze which customers can have conformity documents created and prioritize them by recency. " +
      "When needed, you create the corresponding documents and reports. " +
      "Refer to the stored customer and product data.",
    icon: IconUserCheck,
    apType: "exporter",
    scope: {
      primaryScopeId: "customer",
      visibleScopeIds: ["customer", "product", "component", "substance"],
    },
    chat: {
      systemPromptKey: "customer_conformity",
      knownIntents: [],
    },
    unlockCondition: (s) => s.dbConfigured === true,
    lockRequirements: [
      {
        label: "QMS/REQUIREMENT/CONFIGURE_DATABASE",
        isMet: (d) => d.dbConfigured === true,
        pendingColor: "yellow",
      },
    ],
    getNotifications: (data) => data.customerNewFreshCount ?? 0,
    getPreviewStats: (d) => {
      if (!d.scopes.some((s) => s.id === "customer")) return []
      const total = d.scopeRecordCounts["customer"] ?? 0
      const fresh = d.customerFreshCount ?? 0
      return [
        { label: "QMS/PREVIEW/CUSTOMERS_TOTAL", value: total },
        {
          label: "QMS/PREVIEW/CUSTOMER_CONFORMITIES_READY",
          value: `${fresh}/${total}`,
        },
      ]
    },
    hero: () => "Customer Conformity — Auto-Pilot",
    AktionenTab: CustomerConformityAktionenTab,
    DatenbankTab: SharedDatenbankTab,
  },
  {
    id: "lieferanten-abfragen",
    name: "QMS/AP/SUPPLIER_INQUIRY/NAME",
    description: "QMS/AP/SUPPLIER_INQUIRY/DESCRIPTION",
    systemPrompt:
      "You are a supplier inquiry assistant. " +
      "You analyze which suppliers should update their SVHC declarations and prioritize them by data staleness. " +
      "When needed, you create inquiry letters (REACH Art. 33) and send them to the relevant contacts. " +
      "Refer to the stored supplier, component, and substance data.",
    icon: IconClipboardList,
    apType: "exporter",
    scope: {
      primaryScopeId: "supplier",
      visibleScopeIds: ["supplier", "component", "substance"],
    },
    chat: {
      systemPromptKey: "lieferanten_abfragen",
      knownIntents: [],
    },
    unlockCondition: (s) => s.dbConfigured === true,
    lockRequirements: [
      {
        label: "QMS/REQUIREMENT/CONFIGURE_DATABASE",
        isMet: (d) => d.dbConfigured === true,
        pendingColor: "yellow",
      },
    ],
    getNotifications: (data) =>
      data.ausstehendCountByAp?.["lieferanten-abfragen"] ?? 0,
    getPreviewStats: (d) => {
      if (!d.scopes.some((s) => s.id === "supplier")) return []
      const total = d.scopeRecordCounts["supplier"] ?? 0
      const ausstehend = d.ausstehendCountByAp?.["lieferanten-abfragen"] ?? 0
      return [
        { label: "QMS/PREVIEW/SUPPLIERS_TOTAL", value: total },
        { label: "QMS/PREVIEW/INQUIRIES_PENDING", value: ausstehend },
      ]
    },
    hero: () => "Supplier Inquiries — Auto-Pilot",
    AktionenTab: SupplierInquiryAktionenTab,
    DatenbankTab: SharedDatenbankTab,
  },
  {
    id: "substanz-radar",
    name: "QMS/AP/SUBSTANZ_RADAR/NAME",
    description: "QMS/AP/SUBSTANZ_RADAR/DESCRIPTION",
    systemPrompt:
      "You are the Substance Radar. " +
      "You monitor regulatory changes (ECHA, REACH, RoHS) and identify suppliers " +
      "that may be affected by new substance requirements. " +
      "You create findings with concrete recommendations.",
    icon: IconRadar,
    apType: "monitor",
    scope: {
      primaryScopeId: "supplier",
      visibleScopeIds: ["supplier", "component", "substance"],
    },
    chat: { systemPromptKey: "substanz_radar", knownIntents: [] },
    monitor: {
      rules: [
        {
          field: "external.svhc_candidate_list",
          op: "changes_to",
          value: "new_substance",
        },
        {
          field: "supplier.delivers_component",
          op: "references",
          value: "substance.id",
        },
      ],
      combinator: "any",
      evidenceSources: ["ECHA Candidate List · External Regulatory Signals"],
    },
    unlockCondition: (s) => s.dbConfigured === true,
    lockRequirements: [
      {
        label: "QMS/REQUIREMENT/CONFIGURE_DATABASE",
        isMet: (d) => d.dbConfigured === true,
        pendingColor: "yellow",
      },
    ],
    getNotifications: (data) =>
      data.monitorOpenCountByAp["substanz-radar"] ?? 0,
    getPreviewStats: (d) => {
      const open = d.monitorOpenCountByAp["substanz-radar"] ?? 0
      return [{ label: "QMS/PREVIEW/OPEN_FINDINGS", value: open }]
    },
    hero: () => "Substanz Radar — Auto-Pilot",
    AktionenTab: SubstanzRadarAktionenTab,
    DatenbankTab: SharedDatenbankTab,
  },
]

// ── Template-only extras (Alle tab, not in Aktiv) ─────────────────────────────

const TEMPLATE_EXTRAS: AutoPilotTemplate[] = [
  {
    id: "export-ap",
    name: "QMS/AP/EXPORT/NAME",
    description: "QMS/AP/EXPORT/DESCRIPTION",
    icon: IconFileExport,
    apType: "exporter",
  },
  {
    id: "import-ap",
    name: "QMS/AP/IMPORT/NAME",
    description: "QMS/AP/IMPORT/DESCRIPTION",
    icon: IconFileImport,
    apType: "importer",
  },
]

// ALL_TEMPLATES = 4 active APs (in template form) + 2 extras
export const ALL_TEMPLATES: AutoPilotTemplate[] = [
  ...ACTIVE_APS.map(({ id, name, description, icon, apType }) => ({
    id,
    name,
    description,
    icon,
    apType,
  })),
  ...TEMPLATE_EXTRAS,
]

// ── Lookup helpers ────────────────────────────────────────────────────────────

const AP_MAP = Object.fromEntries(ACTIVE_APS.map((ap) => [ap.id, ap]))

export function getAP(id: string): AutoPilotDef | undefined {
  return AP_MAP[id]
}
